import { Product } from '../../domain/types';
import { COLLECTIONS } from '../../domain/collections';
import { db } from '../../infrastructure/firebaseConfig';
import { cleanObject } from '../../utils/cleanObject';
import { logger } from '../../infrastructure/logger';
import { CacheInvalidator } from '../../utils/cacheInvalidation';
import { REPOSITORIES } from '../../domain/constants';
import {
  collection,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  getDocs,
  runTransaction,
  writeBatch,
  DocumentReference,
  DocumentData,
  WithFieldValue
} from "firebase/firestore";

export class ProductMutations {
  private collectionName = COLLECTIONS.PRODUCTS;

  async addProducts(newProducts: Product[]): Promise<void> {
    try {
      const BATCH_SIZE = REPOSITORIES.FIRESTORE_BATCH_SIZE;

      for (let i = 0; i < newProducts.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = newProducts.slice(i, i + BATCH_SIZE);

        for (const prod of chunk) {
          if (!prod.shopId || typeof prod.shopId !== 'string' || prod.shopId.trim() === '') {
            const err = new Error('PRODUCT_REQUIRES_SHOP: Cada producto debe tener un shopId válido');
            logger.error('[ProductMutations] Product missing shopId', err);
            throw err;
          }
          if (!prod.partnerId || typeof prod.partnerId !== 'string' || prod.partnerId.trim() === '') {
            const err = new Error('PRODUCT_REQUIRES_PARTNER: Cada producto debe tener un partnerId válido');
            logger.error('[ProductMutations] Product missing partnerId', err);
            throw err;
          }
          const cleanProd = cleanObject(prod);
          let docRef: DocumentReference<DocumentData>;

          if (prod.id) {
            docRef = doc(db, this.collectionName, prod.id);
            batch.set(docRef, cleanProd);
          } else {
            docRef = doc(collection(db, this.collectionName));
            batch.set(docRef, cleanProd);
          }
        }

        await batch.commit();
        logger.info(`Batch completed: ${Math.min(i + BATCH_SIZE, newProducts.length)}/${newProducts.length} products`);
      }

      CacheInvalidator.invalidateAll();
    } catch (error) {
      logger.error('[ProductMutations] Error adding products', error);
      throw error;
    }
  }

  async deleteAllProducts(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, this.collectionName));
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      logger.error('[ProductMutations] Error deleting all products', error);
      throw error;
    }
  }

  async updateProduct(updatedProduct: Product): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, updatedProduct.id);
      const finalData: Record<string, unknown> = {};
      Object.entries(updatedProduct).forEach(([key, value]) => {
        if (value !== undefined) {
          finalData[key] = value;
        }
      });
      await updateDoc(docRef, finalData as WithFieldValue<DocumentData>);

      CacheInvalidator.invalidateProduct(updatedProduct.id);
    } catch (error) {
      logger.error(`[ProductMutations] Error updating product ${updatedProduct.id}`, error);
      throw error;
    }
  }

  async decreaseStock(productId: string, amount: number): Promise<{ success: boolean; error?: string }> {
    const docRef = doc(db, this.collectionName, productId);

    try {
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        if (!docSnap.exists()) {
          const err = new Error('Producto no encontrado');
          logger.error('[ProductMutations] Product not found in decreaseStock', err);
          throw err;
        }

        const data = docSnap.data();
        const currentStock = data.stock || 0;

        if (currentStock < amount) {
          const err = new Error(`Stock insuficiente. Disponible: ${currentStock}, Solicitado: ${amount}`);
          logger.error('[ProductMutations] Insufficient stock', err);
          throw err;
        }

        transaction.update(docRef, {
          stock: currentStock - amount,
          updatedAt: new Date().toISOString()
        });
      });

      CacheInvalidator.invalidateProduct(productId);
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al actualizar stock';
      logger.error('[ProductMutations] Error in decreaseStock', error);
      return { success: false, error: message };
    }
  }

  async createProduct(product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    if (!product.shopId || typeof product.shopId !== 'string' || product.shopId.trim() === '') {
      const err = new Error('PRODUCT_REQUIRES_SHOP: Cada producto debe tener un shopId válido');
      logger.error('[ProductMutations] Missing shopId in createProduct', err);
      throw err;
    }
    if (!product.partnerId || typeof product.partnerId !== 'string' || product.partnerId.trim() === '') {
      const err = new Error('PRODUCT_REQUIRES_PARTNER: Cada producto debe tener un partnerId válido');
      logger.error('[ProductMutations] Missing partnerId in createProduct', err);
      throw err;
    }

    const productData = {
      ...product,
      salesCount: 0,
      viewCount: 0,
      status: product.status || 'pending_review',
      approvalStatus: 'pending_review' as const
    };

    const docRef = await addDoc(collection(db, this.collectionName), productData);
    const productId = docRef.id;

    CacheInvalidator.invalidateProduct(productId);
    CacheInvalidator.invalidateShop(product.shopId);

    return productId;
  }
}

export const productMutations = new ProductMutations();
